import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Auth } from '@freelancer/auth';
import { Feature, FeatureFlagsService } from '@freelancer/feature-flags';
import { LocalStorage } from '@freelancer/local-storage';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import type { UiConfig } from '@freelancer/ui/ui.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import { WHITELISTED_URLS } from './find-trucks-announcement-prompt.types';

@UntilDestroy({ className: 'FindTrucksAnnouncementPromptService' })
@Injectable({
  providedIn: 'root',
})
export class FindTrucksAnnouncementPromptService {
  private platformId = inject(PLATFORM_ID);
  private uiConfig = inject<UiConfig>(UI_CONFIG);
  private featureFlagsService = inject(FeatureFlagsService);
  private auth = inject(Auth);
  private localStorage = inject(LocalStorage);

  private readonly ONE_DAY_IN_MS = 1000 * 60 * 60 * 24;
  private readonly ONE_WEEK_IN_MS = this.ONE_DAY_IN_MS * 7;

  /**
   * TODO: T337534 The announcement runs for around 60 days after release
   */
  private readonly ANNOUNCEMENT_END_DATE = new Date('2026-10-16');

  private isFindTrucksAnnouncementDisabled = false;

  // Only to be called by FindTrucksAnnouncementPromptOverridesGuard
  setIsFindTrucksAnnouncementDisabled(disabled: boolean): void {
    this.isFindTrucksAnnouncementDisabled = disabled;
  }

  /**
   * Checks if the Find Trucks announcement modal should be shown based on:
   * - If the announcement has not been disabled (e.g. by an e2e override)
   * - If the user is client side (not SSR)
   * - If the user is actually on Loadshift
   * - If the current URL is whitelisted
   * - If the announcement campaign has not ended yet
   * - If the user has not already seen the announcement within the last week
   * - If the user is logged in
   * - If the user availability feature flag is toggled on
   *
   * @returns resolves to true if all the above factors are true, and false otherwise.
   */
  async shouldShowFindTrucksAnnouncement(url: string): Promise<boolean> {
    if (
      this.isFindTrucksAnnouncementDisabled ||
      !isPlatformBrowser(this.platformId) ||
      this.uiConfig.theme !== 'freightlancer' ||
      !this.isAllowedUrl(url)
    ) {
      return false;
    }

    if (new Date() >= this.ANNOUNCEMENT_END_DATE) {
      return false;
    }

    const lastSeen = await firstValueFrom(
      this.localStorage
        .get('findTrucksAnnouncementLastSeen')
        .pipe(untilDestroyed(this)),
    );
    if (lastSeen !== undefined && Date.now() - lastSeen < this.ONE_WEEK_IN_MS) {
      return false;
    }

    const isLoggedIn = await firstValueFrom(
      this.auth.isLoggedIn().pipe(untilDestroyed(this)),
    );
    if (!isLoggedIn) {
      return false;
    }

    return firstValueFrom(
      this.featureFlagsService
        .getFlag(Feature.USER_AVAILABILITY)
        .pipe(untilDestroyed(this)),
    );
  }

  /**
   * Records that the Find Trucks announcement modal has just been shown, so it
   * is not shown again until at least a week later.
   */
  async markFindTrucksAnnouncementSeen(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    await this.localStorage.set('findTrucksAnnouncementLastSeen', Date.now());
  }

  private isAllowedUrl(url: string): boolean {
    return WHITELISTED_URLS.some(
      allowedUrl =>
        url.startsWith(allowedUrl) ||
        url.startsWith(`/freightlancer${allowedUrl}`),
    );
  }
}
